/**
 * L'orchestration documentaire — un seul chemin décide quoi afficher (#1332).
 *
 * Les appelants expriment une **intention** au lieu d'ordonner eux-mêmes une
 * série de setters et de compteurs. Deux existent : restaurer le brouillon du
 * fil affiché (`/assistant`), et l'amorcer avant qu'un agent ne prenne le
 * document d'un fil qui n'en a encore aucun dans l'onglet (#1586). Elles
 * partagent la recherche et le tri-état ; seule la seconde n'ouvre rien, parce
 * que son appelant ouvre après la résidence.
 *
 * ## Décision pure, effets ordonnés
 *
 * La décision de source vit dans `canvasRestaurationDeBrouillon` : elle ne
 * touche ni le réseau, ni un store, ni SuperDoc. Ce module ordonne ensuite les
 * effets, dans cet ordre et jamais un autre : recherche → décision →
 * préparation de la session logique → ouverture. Le **téléchargement** du
 * contenu exact et le **montage** restent la transaction de
 * `CanvasResidenceCapacityCoordinator` : c'est déjà la seule autorité qui
 * refroidit une victime, précharge le brouillon exact et revalide après chaque
 * `await`. En ouvrir un second ici rendrait deux GET pour un document et deux
 * réponses susceptibles de se contredire.
 *
 * ## Le tri-état, et pourquoi il ne peut pas vivre dans un montage
 *
 * Les deux issues d'échec (`recherche-illisible`, `contenu-illisible`) sont
 * publiées dans `canvasRestaurationIndisponible`, hors React et hors scope :
 * l'échec dont elles parlent est précisément celui qui empêche un montage
 * d'exister.
 *
 * ## Frappe pendant le réseau
 *
 * La garde n'est pas le compteur global `dirtyTick` — orphelin, retiré par ce
 * ticket. C'est la **version du contrôleur de brouillon** de la session exacte,
 * comparée avant et après le GET (`canvasResidenceCapacity.preloadTarget`),
 * plus la **cible capturée** avant toute attente. Une frappe ou une nouvelle
 * intention arrivée pendant le trajet invalide donc le résultat au lieu de
 * remplacer le contenu courant.
 */

import type { CanvasDraft } from '@hector/shared-types';
import { canvasDraftApi } from '../api/canvasDraft.api';
import { canvasDocumentRuntime, type CauseDOccupationDocumentaire } from './canvasDocumentRuntime';
import type { CanvasDocumentScope } from './canvasDocumentScope';
import { openCanvas } from './canvasOpening';
import {
  deciderApresLaRecherche,
  etatDeContenuIllisible,
  rechercheDepuisUnRejet,
  type RechercheDeBrouillon,
  type RestaurationIndisponible,
} from './canvasRestaurationDeBrouillon';
import {
  declarerLaRestaurationIndisponible,
  leverLaRestaurationIndisponible,
} from './canvasRestaurationIndisponible';

/** Ce que l'appelant VEUT. Jamais la suite de setters qui y mène. */
export type IntentionDocumentaire =
  | {
    readonly kind: 'restaurer-le-brouillon';
    readonly conversationId: string;
    /** Abandonne la restauration si l'avocat a déjà changé de fil. */
    readonly signal?: AbortSignal;
  }
  | {
    /**
     * Un agent va prendre le document de ce fil (`WebSocketPool`,
     * `openCanvasForAgentWrite` et `canvas_admission_prepare`). Sans amorce, un
     * fil rouvert hors de `/assistant` montait une page VIERGE, que la première
     * écriture enregistrait sous un nouveau `draftId` — et le serveur supprimait
     * alors l'ancien brouillon du fil (#1586).
     */
    readonly kind: 'amorcer-le-brouillon-avant-admission';
    readonly conversationId: string;
  };

export type ResultatDIntention =
  | { readonly issue: 'brouillon-pose'; readonly draftId: string }
  | { readonly issue: 'brouillon-deja-en-place'; readonly draftId: string }
  | { readonly issue: 'aucun-brouillon' }
  /** Amorce seulement : le fil a déjà un document, logique ou monté — rien n'est cherché. */
  | { readonly issue: 'document-deja-dans-l-onglet' }
  /**
   * Amorce seulement : le brouillon trouvé n'a pas pu être attaché au document
   * du fil (fil supprimé pendant la recherche, par exemple). Distinct de
   * `refusee` — une ouverture concurrente — parce que la suite à donner diffère :
   * il n'y a ici aucune ouverture dont attendre la fin.
   */
  | { readonly issue: 'amorce-impossible'; readonly raison: string }
  | { readonly issue: 'restauration-indisponible'; readonly etat: RestaurationIndisponible }
  | { readonly issue: 'refusee'; readonly raison: string };

export interface OrchestrationDependencies {
  readonly chercherLeBrouillon: (conversationId: string) => Promise<CanvasDraft | null>;
  // ⚠️ `getDocumentSessionIdForConversation` a quitté cette liste sans rien y
  // remplacer (#1336) : elle traduisait une conversation en identifiant de
  // document, et il n'y a plus de traduction à faire — `getScope` et
  // `getDraftController` s'adressent directement par la conversation.
  readonly runtime: Pick<
    typeof canvasDocumentRuntime,
    | 'getScope'
    | 'getDraftController'
    | 'prepareRestoredDraftSession'
    | 'decrireLOccupationDuDocument'
  >;
  readonly ouvrirLeCanvas: (raison: string) => void;
}

/**
 * Borne de la recherche du brouillon d'un fil, avant toute prise du canvas par
 * l'agent (#1596).
 *
 * **Ce n'est pas un budget partagé avec le montage, c'est une échéance de
 * refus.** Si la recherche n'a pas répondu à temps, rien n'est monté et l'agent
 * reçoit le refus nommé « could not be looked up ». Le montage n'a lieu que si
 * la recherche a abouti — auquel cas elle a coûté quelques dizaines de
 * millisecondes, pas des secondes.
 *
 * **Dérivation — c'est la borne backend la plus COURTE qui commande**, pas la
 * plus large. L'amorce sert trois familles d'appelants, dont les attentes
 * backend diffèrent (relevées le 2026-09-18, canvas relevées le 2026-10-12) :
 *   - `canvas_perform_action` → `MUTATION_TIMEOUT_MS = 20_000` au plus court
 *     (écriture ciblée ; 120 s et plus pour les autres verbes) ← **la plus courte**, et
 *     `canvas_inspect` → `INSPECT_TIMEOUT_MS = 45_000`
 *     (apps/backend/.../tools/backend/SuperDocCanvasTools.ts)
 *   - `open_canvas` → `CANVAS_OPEN_ACK_TIMEOUT_MS = 30_000`, et
 *     `canvas_admission_prepare` → `waitForCanvasAdmission(..., timeoutMs = 30_000)`
 *     (apps/backend/.../websocket/SessionState.ts:267)
 *
 * 8 s laisse ~12 s pour que la trame de refus remonte avant que la plus courte de
 * ces attentes n'expire, tout en restant deux ordres de grandeur au-dessus de la
 * latence réelle d'un petit GET JSON. Un refus nommé vaut infiniment mieux qu'un
 * timeout backend générique : l'agent sait quoi dire à l'avocat.
 *
 * Une recherche lente suivie d'un montage complet partage l'échéance unique du
 * gestionnaire appelant (`CANVAS_OPEN_BROWSER_DEADLINE_MS` pour `open_canvas`).
 */
export const CANVAS_DRAFT_LOOKUP_TIMEOUT_MS = 8_000;

const dependancesParDefaut: OrchestrationDependencies = {
  // AbortSignal.timeout borne le GET à l'intérieur du délai d'admission backend
  // (30 s) en laissant la marge nécessaire au montage et au transport (#1596).
  // Si le signal expire, getDraftByConversation lève une DOMException (TimeoutError)
  // que traduireLaRecherche capture via rechercheDepuisUnRejet → recherche-illisible.
  chercherLeBrouillon: (conversationId) =>
    canvasDraftApi.getDraftByConversation(
      conversationId,
      AbortSignal.timeout(CANVAS_DRAFT_LOOKUP_TIMEOUT_MS),
    ),
  runtime: canvasDocumentRuntime,
  ouvrirLeCanvas: openCanvas,
};

/**
 * Une recherche par conversation, partagée entre l'effet de l'écran et un
 * clic humain concurrent.
 *
 * Le contenu n'est jamais mis en cache : seule la promesse EN VOL l'est. Après
 * son dénouement, une nouvelle intention relit le serveur. Cela ferme la fenêtre
 * où le clic montait un vierge pendant que l'effet cherchait encore le brouillon,
 * sans transformer ce module en cache documentaire.
 */
const recherchesParDefautEnVol = new Map<string, Promise<RechercheDeBrouillon>>();

/**
 * Point d'entrée unique. `switch` exhaustif : une nouvelle intention ajoutée plus
 * tard doit faire rougir la compilation, jamais tomber dans un `else` qui ferait
 * « ce qu'on faisait avant ».
 */
export async function executerLIntentionDocumentaire(
  intention: IntentionDocumentaire,
  deps: OrchestrationDependencies = dependancesParDefaut,
): Promise<ResultatDIntention> {
  switch (intention.kind) {
    case 'restaurer-le-brouillon':
      return restaurerLeBrouillon(intention.conversationId, deps, intention.signal);
    case 'amorcer-le-brouillon-avant-admission':
      return amorcerAvantAdmission(intention.conversationId, deps);
  }
}

/**
 * Prépare le document du fil pour un clic humain, sans déployer le panneau.
 *
 * Le geste canonique prend le fil juste après. Laisser la restauration appeler
 * `openCanvas` avant cette prise réveillerait `CanvasResidence` avec une scène
 * encore ancienne ; supprimer seulement cet effet conserve la même recherche,
 * la même décision et la même préparation logique.
 */
export function preparerLeBrouillonAvantOuvertureHumaine(
  conversationId: string,
): Promise<ResultatDIntention> {
  return restaurerLeBrouillon(conversationId, {
    ...dependancesParDefaut,
    ouvrirLeCanvas: () => undefined,
  });
}

/**
 * Le checkpoint initial d'une source, ordonné par le montage qui l'expose.
 *
 * Une nouvelle source ouverte confirme un checkpoint **après le vrai ready**,
 * même sans frappe : c'est la contrepartie de la restauration, sans laquelle un
 * rechargement d'onglet perd la pièce qu'Hector venait d'ouvrir (#1346).
 *
 * Trois raisons de le brancher ici plutôt que dans les chemins de chargement :
 * les deux branches de `canvas_load_template_request` (froide et chaude) ne
 * passent pas par le même contrat, le geste humain « Ouvrir dans le Canvas » en
 * emprunte un troisième, et seul le signal `ready` atteste que l'éditeur peut
 * réellement exporter.
 *
 * ⚠️ **Une page blanche n'est PAS une source.** Persister d'office un document
 * que l'avocat n'a ni ouvert ni touché lui fabriquerait un brouillon qu'il n'a
 * pas demandé ; la garde est la présence d'un `templateOverride` posé.
 *
 * ⚠️ **Et la DEUXIÈME source d'un fil est une source aussi** (#1334/D2). Le
 * contrôleur vit plus longtemps que ses sources — un remplacement A → B garde
 * le même scope, donc le même contrôleur. C'est donc ce montage-ci, seul à voir
 * la présentation, qui lui dit *quelle* source il expose : ses octets, et s'ils
 * viennent du serveur — auquel cas ils n'ont pas à y retourner (voir
 * `cesOctetsViennentDuServeur`).
 *
 * ⚠️ **La source, pas le montage** (D2 de #1337) : ce listener rejoue à CHAQUE
 * montage prêt — remplacement, réhydratation, et sous D1 chaque éditeur que
 * SuperDoc reconstruit. Seuls des octets NEUFS arment un checkpoint ; une
 * époque neuve n'en prouve aucun.
 */
export function installerLaConfirmationDuCheckpointInitial(
  runtime: Pick<typeof canvasDocumentRuntime, 'onSessionReady' | 'getScope'> = canvasDocumentRuntime,
): () => void {
  return runtime.onSessionReady((session) => {
    const scope = runtime.getScope(session.conversationId);
    if (!scope) return;
    const presentation = scope.getPresentation();
    if (!presentation?.templateOverride) return;
    // Une source montée rend le verdict d'échec SANS OBJET : il parlait d'un
    // document qu'on n'avait pas pu rouvrir, et il y en a un maintenant. Le
    // garder ferait cohabiter « ce document n'a pas pu être rouvert » avec le
    // document à l'écran.
    // Plus de garde de nullité : `CanvasRuntimeSession.conversationId` a cessé
    // d'être nullable avec l'indexation par conversation (#1336), donc le test
    // rendrait la même valeur avec le défaut et sans lui.
    leverLaRestaurationIndisponible(session.conversationId);
    const controleur = scope.getDraftController();
    controleur.confirmerLeCheckpointInitial({
      octets: presentation.templateOverride,
      estLeBrouillonDurable: cesOctetsViennentDuServeur(scope, presentation),
    });
  });
}

/**
 * Ces octets viennent-ils du SERVEUR ? — la seule question qui compte ici.
 *
 * ⚠️ **`activeDraft` seul ne suffit PAS**, et le croire fabriquait une écriture
 * à chaque aller-retour LRU (trouvé en revue, #1334). La réhydratation d'un
 * résident refroidi (`canvasResidenceCapacity.preloadTarget`) télécharge le
 * brouillon durable — **des octets neufs** — et reprend `activeDraft` du seed
 * logique — donc `null` pour toute pièce ouverte par
 * `replaceCanvasResidentTemplate`. La clé de source voyait une source neuve,
 * armait, exportait et renvoyait au serveur les octets qu'il venait de rendre.
 * Répété à chaque refroidissement, ce round-trip DOCX fait dériver le document
 * en base.
 *
 * Le second fait est déjà posé au bon moment par le montage froid
 * (`markColdHydratedDraft`, consommé aussi par `RedactionPanel`) : « ce montage
 * a été semé depuis le brouillon X ». Confronté au `draftId` courant du
 * contrôleur, il distingue une réhydratation d'une vraie nouvelle source.
 */
function cesOctetsViennentDuServeur(
  scope: Pick<CanvasDocumentScope, 'wasColdHydratedFromDraft' | 'getDraftController'>,
  presentation: { readonly activeDraft: CanvasDraft | null },
): boolean {
  if (presentation.activeDraft !== null) return true;
  const draftId = scope.getDraftController().getSnapshot().draftId;
  return draftId !== null && scope.wasColdHydratedFromDraft(draftId);
}

/**
 * Signale qu'un montage n'a pas pu récupérer le contenu exact de son brouillon.
 *
 * Appelé par la résidence, qui est l'autorité du téléchargement : la décision
 * reste celle du module pur, seule sa publication change de lieu.
 */
export function signalerUnContenuDeBrouillonIllisible(input: {
  readonly conversationId: string;
  readonly draftId: string;
  readonly cause: unknown;
}): RestaurationIndisponible {
  const etat = etatDeContenuIllisible(
    input.conversationId,
    input.draftId,
    input.cause instanceof Error ? input.cause.message : String(input.cause),
  );
  declarerLaRestaurationIndisponible(etat);
  return etat;
}

/**
 * La recherche du brouillon d'un fil, traduite en issue du tri-état.
 *
 * Partagée par les deux intentions : une seconde traduction finirait par
 * confondre « rejet » et « aucun brouillon » d'un côté seulement — et c'est
 * justement la confusion qui ouvre une page blanche sur un fil qui en a un.
 */
async function rechercherLeBrouillon(
  conversationId: string,
  deps: OrchestrationDependencies,
): Promise<RechercheDeBrouillon> {
  if (deps.chercherLeBrouillon === dependancesParDefaut.chercherLeBrouillon) {
    const existante = recherchesParDefautEnVol.get(conversationId);
    if (existante) return existante;
    const recherche = traduireLaRecherche(conversationId, deps.chercherLeBrouillon)
      .finally(() => {
        // Une intention plus récente peut avoir pris la place : elle seule doit
        // alors retirer sa propre promesse.
        if (recherchesParDefautEnVol.get(conversationId) === recherche) {
          recherchesParDefautEnVol.delete(conversationId);
        }
      });
    recherchesParDefautEnVol.set(conversationId, recherche);
    return recherche;
  }
  return traduireLaRecherche(conversationId, deps.chercherLeBrouillon);
}

/**
 * Double borne de la recherche, et c'est délibéré.
 *
 * `dependancesParDefaut.chercherLeBrouillon` passe déjà un
 * `AbortSignal.timeout(CANVAS_DRAFT_LOOKUP_TIMEOUT_MS)` au `fetch`. Cette
 * seconde borne le double ici parce qu'une promesse restée en suspens gèle
 * l'admission entière — et le signal ne protège QUE si le transport l'honore :
 * une dépendance injectée en test, un polyfill, une couche qui perd l'option
 * `signal` suffisent à le perdre. C'est exactement le raisonnement déjà tenu
 * pour `CANVAS_DRAFT_TRANSFER_TIMEOUT_MS` (canvasDraft.api.ts) ; les deux
 * bornes partagent donc la MÊME constante, jamais une copie.
 *
 * À l'échéance, le rejet remonte dans `rechercheDepuisUnRejet` et devient
 * `recherche-illisible` : l'appelant rend alors à l'agent le refus nommé
 * « could not be looked up », et rien n'est monté (#1596).
 */
async function courseContreLEcheance(
  recherche: Promise<CanvasDraft | null>,
  conversationId: string,
): Promise<CanvasDraft | null> {
  let echeance: ReturnType<typeof setTimeout> | undefined;
  try {
    return await Promise.race([
      recherche,
      new Promise<never>((_, rejeter) => {
        echeance = setTimeout(
          () => rejeter(new Error(
            `canvas-drafts: la recherche du brouillon de conversationId=${conversationId} n'a pas répondu en `
            + `${CANVAS_DRAFT_LOOKUP_TIMEOUT_MS} ms. Rien n'est monté ; l'appelant doit rendre un refus nommé `
            + 'plutôt que d\'ouvrir une page vierge qui écraserait le brouillon enregistré.',
          )),
          CANVAS_DRAFT_LOOKUP_TIMEOUT_MS,
        );
      }),
    ]);
  } finally {
    if (echeance !== undefined) clearTimeout(echeance);
  }
}

async function traduireLaRecherche(
  conversationId: string,
  chercher: OrchestrationDependencies['chercherLeBrouillon'],
): Promise<RechercheDeBrouillon> {
  // Annoté, pas inféré : c'est le TYPE qui garantit que les trois issues du
  // tri-état sont les seules valeurs possibles ici, et donc que le `switch`
  // exhaustif de l'appelant garde quelque chose.
  try {
    const draft = await courseContreLEcheance(chercher(conversationId), conversationId);
    // ⚠️ Le SEUL cas d'absence : une réponse REÇUE qui porte `draft: null`.
    return draft
      ? { issue: 'brouillon-trouve', draft }
      : { issue: 'aucun-brouillon' };
  } catch (error) {
    return rechercheDepuisUnRejet(error);
  }
}

/**
 * Les causes qui disent qu'un éditeur EXISTE ou va exister pour ce fil : y poser
 * un brouillon changerait le document d'un montage sans époque neuve, ou
 * diverger de ce que la transaction en vol est en train de monter.
 */
const CAUSES_PHYSIQUES: ReadonlySet<CauseDOccupationDocumentaire> = new Set([
  'monte', 'chaud', 'hydratation', 'transaction-de-residence', 'remplacement-exclusif',
]);

/**
 * Amorce le brouillon d'un fil AVANT qu'un agent ne demande sa résidence (#1586).
 *
 * N'ouvre rien et ne monte rien : elle prépare la session LOGIQUE, et la
 * transaction de résidence que l'appelant ouvre ensuite télécharge le contenu
 * exact (`canvasResidenceCapacity.preloadTarget`) — aucun second GET de contenu.
 *
 * ⚠️ **Le premier contrôle est sans réseau, et c'est le coût de l'amorce** : un
 * fil qui a déjà un document dans l'onglet, logique (refroidi, amorcé) ou monté,
 * n'est jamais recherché. Seul le premier accès agent d'un fil, par onglet, paie
 * la recherche.
 */
async function amorcerAvantAdmission(
  conversationId: string,
  deps: OrchestrationDependencies,
): Promise<ResultatDIntention> {
  if (deps.runtime.getDraftController(conversationId) !== null || deps.runtime.getScope(conversationId) !== null) {
    return { issue: 'document-deja-dans-l-onglet' };
  }

  // Même levée que la restauration : une amorce qui aboutit ne doit pas laisser
  // survivre le verdict « restauration impossible » d'une tentative précédente.
  leverLaRestaurationIndisponible(conversationId);
  const decision = deciderApresLaRecherche(conversationId, await rechercherLeBrouillon(conversationId, deps));
  switch (decision.geste) {
    case 'declarer-indisponible':
      declarerLaRestaurationIndisponible(decision.etat);
      return { issue: 'restauration-indisponible', etat: decision.etat };
    case 'ouvrir-vierge':
      return { issue: 'aucun-brouillon' };
    case 'telecharger-le-brouillon':
      break;
  }

  // RELU APRÈS L'ATTENTE : pendant la recherche, une restauration ou une autre
  // ouverture du même fil a pu préparer ou monter son document.
  const draft = decision.draft;
  const controleur = deps.runtime.getDraftController(conversationId);
  if (controleur?.getSnapshot().draftId === draft.id) {
    return { issue: 'brouillon-deja-en-place', draftId: draft.id };
  }
  const causes = deps.runtime.decrireLOccupationDuDocument(conversationId);
  if (controleur !== null || causes.some((cause) => CAUSES_PHYSIQUES.has(cause))) {
    return {
      issue: 'refusee',
      raison:
        `another opening of this conversation's document started while its saved draft draftId=${draft.id} `
        + `was looked up (${causes.length > 0 ? causes.join(', ') : 'logical document already prepared'})`,
    };
  }

  try {
    deps.runtime.prepareRestoredDraftSession(draft);
  } catch (error) {
    return {
      issue: 'amorce-impossible',
      raison:
        `the saved draft draftId=${draft.id} could not be attached to this conversation's document `
        + `(${error instanceof Error ? error.message : String(error)})`,
    };
  }
  return { issue: 'brouillon-pose', draftId: draft.id };
}

async function restaurerLeBrouillon(
  conversationId: string,
  deps: OrchestrationDependencies,
  signal?: AbortSignal,
): Promise<ResultatDIntention> {
  // Une nouvelle tentative efface le verdict précédent : sinon le message de
  // restauration impossible survivrait à la restauration qui vient d'aboutir.
  leverLaRestaurationIndisponible(conversationId);

  const recherche = await rechercherLeBrouillon(conversationId, deps);

  // La cible a été capturée avant l'attente : une navigation pendant le trajet
  // abandonne ce résultat au lieu de préparer une session que plus personne ne
  // regarde. La déclaration d'échec, elle, reste indexée par conversation et
  // n'a donc pas besoin de cette garde.
  if (signal?.aborted) {
    return {
      issue: 'refusee',
      raison: `la restauration de la conversation ${conversationId} a été abandonnée : l'avocat a changé de fil pendant la recherche`,
    };
  }

  const decision = deciderApresLaRecherche(conversationId, recherche);
  if (decision.geste === 'declarer-indisponible') {
    declarerLaRestaurationIndisponible(decision.etat);
    return { issue: 'restauration-indisponible', etat: decision.etat };
  }

  switch (decision.geste) {
    case 'ouvrir-vierge':
      return { issue: 'aucun-brouillon' };
    case 'telecharger-le-brouillon':
      return preparerLaRestauration(conversationId, decision.draft, deps);
  }
}

/**
 * Prépare la session logique du brouillon trouvé, puis ouvre le canvas.
 *
 * Le contenu exact est téléchargé par la transaction de résidence, qui possède
 * déjà la garde « la version du contrôleur n'a pas bougé pendant le GET ».
 */
function preparerLaRestauration(
  conversationId: string,
  draft: CanvasDraft,
  deps: OrchestrationDependencies,
): ResultatDIntention {
  const scope = deps.runtime.getScope(conversationId);
  const presentation = scope?.getPresentation() ?? null;
  const controleur = deps.runtime.getDraftController(conversationId)?.getSnapshot() ?? null;

  if (presentation?.activeDraft?.id === draft.id || controleur?.draftId === draft.id) {
    deps.ouvrirLeCanvas(`brouillon déjà préparé — conversation ${conversationId}`);
    return { issue: 'brouillon-deja-en-place', draftId: draft.id };
  }

  // ⚠️ **Un document en cours d'admission ou de montage n'est jamais repris**
  // (#1586). Les gardes de non-écrasement ci-dessous ne voyaient qu'une source,
  // un `draftId` ou une frappe : un éditeur VIERGE que l'agent venait de monter
  // passait, recevait les métadonnées du brouillon, et la clé React reconstruisait
  // SuperDoc sous une époque neuve — la requête suivante de l'agent tombait en
  // `DOCUMENT_SESSION_CHANGED`, ou le document partait sous le mauvais `draftId`.
  //
  // ⚠️ **L'épingle SEULE est acceptée** : un `redaction_job_started` rejoué après
  // un rechargement épingle un fil qui n'a encore aucun document, et une amorce
  // agent épingle avant sa recherche. La session logique posée ici ne crée ni
  // éditeur ni époque, et reste idempotente avec l'amorce ; la refuser rouvrirait
  // précisément la page vierge que l'amorce ferme.
  const causes = deps.runtime.decrireLOccupationDuDocument(conversationId);
  const travailSurUnDocumentLogique = controleur !== null
    && causes.some((cause) => cause === 'epingle' || cause === 'activite');
  if (causes.some((cause) => CAUSES_PHYSIQUES.has(cause)) || travailSurUnDocumentLogique) {
    return {
      issue: 'refusee',
      raison:
        `la conversation ${conversationId} a un document en cours d'admission ou de montage `
        + `(${causes.join(', ')}) ; le brouillon ${draft.id} n'est pas posé par-dessus — `
        + 'aucune reconstruction d\'éditeur, aucune époque neuve',
    };
  }

  // Gardes de NON-ÉCRASEMENT, lues sur la session exacte du fil et jamais sur
  // le résident visible : au retour dans A pendant que B est à l'écran, seul
  // l'état de A peut décider. Un document LOGIQUE déjà porteur d'une identité
  // durable ou d'une frappe non enregistrée ne se fait pas remplacer par un
  // brouillon serveur. Les gardes qui lisaient la présentation d'un scope
  // (source posée, brouillon actif) sont parties avec #1586 : un scope monté est
  // refusé juste au-dessus, elles ne pouvaient plus rien décider.
  if (
    controleur?.draftId != null
    || (controleur != null && controleur.version > controleur.savedVersion)
  ) {
    return {
      issue: 'refusee',
      raison:
        `la conversation ${conversationId} porte déjà un document ouvert ou modifié ; `
        + `le brouillon ${draft.id} n'est pas posé par-dessus`,
    };
  }

  // Aucune publication vers un scope monté : un fil qui en porte un vient d'être
  // refusé ci-dessus. La présentation vit dans les ressources LOGIQUES, que la
  // transaction de résidence lit au montage.
  deps.runtime.prepareRestoredDraftSession(draft);
  deps.ouvrirLeCanvas(`brouillon restauré — conversation ${conversationId}`);
  return { issue: 'brouillon-pose', draftId: draft.id };
}
